import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';

import { useTheme } from 'contexts/ThemeContext';

import { MARK_DELETE_KEY } from 'utils/functions';
import moment from 'utils/moment';

import { Text } from 'components/DataDisplay';
import { DatePicker } from 'components/DatePicker';
import {
  AsyncAutocomplete,
  AutocompleteTextField,
  Button,
  CustomSelect,
  IconButton,
  LocationSearchInput,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Pagination } from 'components/Navigation';
import { Paper } from 'components/Surfaces';

const withSelectedOptions = (baseOptions, selectedValues) => {
  const opts = Array.isArray(baseOptions) ? baseOptions : [];
  const selected = Array.isArray(selectedValues) ? selectedValues : [];

  const missingOptions = selected.reduce((acc, selectedValue) => {
    const optionKey =
      selectedValue?.key ?? selectedValue?.value ?? selectedValue;
    const alreadyPresent = opts.some(
      option => (option?.key ?? option?.value) === optionKey
    );

    if (optionKey && !alreadyPresent) {
      acc.push({
        key: optionKey,
        label: selectedValue?.label ?? optionKey
      });
    }

    return acc;
  }, []);

  return missingOptions.length ? [...opts, ...missingOptions] : opts;
};

export const ListField = props => {
  const {
    disabled,
    options,
    actionTxt,
    deleteAction,
    markDelete,
    onChange,
    values,
    formatedValues,
    fieldsFormat,
    sortValues,
    searchOptionsKeys,
    textFieldOnTop,
    selectNbMax,
    deleteIcon,
    useWarning,
    deleteFunc,
    warningColor,
    placeholder,
    variant,
    inputs,
    isRequireds,
    tooltips,
    labels,
    units,
    bgColor,
    hideKeys,
    valueUnique,
    keyEnter,
    handleFavorite,
    favoriteValue,
    initialAction = false,
    rowPerPage = 5,
    onChangeInput
  } = props;
  const { themeColors } = useTheme();
  const ds = themeColors.designSystem;

  const [action, setAction] = useState(initialAction);
  const [list, setlists] = useState(values);
  const [savedValues] = useState(values);
  const [listUpdate, setListUpdate] = useState({});
  const [fields, setFields] = useState({});
  const [requireds] = useState(
    ['autocomplete', 'asyncAutoComplete'].includes(variant)
      ? isRequireds
      : inputs.map((_, index) =>
          typeof isRequireds[index] === 'boolean' ? isRequireds[index] : true
        )
  );

  const [page, setPage] = React.useState(1);
  const [searchTxt, setSearchTxT] = React.useState('');
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const onSearchTxtChange = value => {
    setSearchTxT(value);
    setPage(1);
  };

  const updateList = (datas, upsert = null) => {
    if (upsert) {
      onChange({ ...datas, tag: upsert });
      setlists({ ...datas, tag: upsert });
    } else {
      const sortedList = sortValues(datas);
      onChange(sortedList);
      setlists(sortedList);
    }
  };

  const onClick = () => {
    // init switch state
    if (variant === 'customFields') {
      const newState = inputs.reduce((acc, el, index) => {
        if (el === 'Switch') {
          acc[options[index]] = false;
        }
        return acc;
      }, {});

      setFields(newState);
    }
    setAction(true);
  };

  const onAutoCompliteChange = value => {
    const newList = list;
    newList.push(value);
    updateList(newList);
    setAction(false);
  };

  const onAddFields = () => {
    const newList = [...list];

    let block = false;
    const sortFields = options.reduce((acc, el, index) => {
      if (inputs[index] === 'TextFieldArray' && fields.tmp) {
        if (!fields[el]) {
          fields[el] = [];
        }
        fields[el].push(fields.tmp);
      }
      acc[el] = fields[el];
      if (requireds[index] && typeof acc[el] === 'undefined') {
        block = true;
      }
      return acc;
    }, {});
    if (!block) {
      newList.push(sortFields);
      setFields({});
      updateList(newList);
      setAction(false);
    }
  };

  const handleChange = (itemKey, isArray) => value => {
    if (!value && typeof value !== 'boolean') {
      return;
    }

    const text = typeof value === 'string' ? value.trim() : value;

    if (isArray) {
      setFields(prevState => {
        const states = { ...prevState };
        if (!states[itemKey]) {
          states[itemKey] = [];
        }
        delete states.tmp;
        states[itemKey].push(text);
        return states;
      });
      return;
    }

    setFields(prevState => {
      const states = { ...prevState };

      states[itemKey] = text;
      return states;
    });
  };

  const handleDelete = (itemKey, value) => () => {
    setFields(prevState => {
      const states = { ...prevState };
      states[itemKey] = states[itemKey].filter(el => el !== value);
      return states;
    });
  };

  const handleDeleteUpdate = item => (itemKey, value) => () => {
    // eslint-disable-next-line no-param-reassign
    item[itemKey] = item[itemKey].filter(el => el !== value);
    setListUpdate({ ...listUpdate });
  };

  const filterDelete =
    deleteFunc ||
    ((data, allData) => {
      if (data._id) {
        return allData.filter(item => item._id !== data._id);
      }

      if (data.id) {
        return allData.filter(item => item.id !== data.id);
      }

      // cas des critere et categories
      if (data.key) {
        return allData.filter(item => item.key !== data.key);
      }

      return allData.filter(item => item !== data);
    });

  const filterData = datas => {
    const toTest = searchTxt
      .toString()
      .toLowerCase()
      .trim();
    if (toTest.length === 0) {
      return datas;
    }

    const filtered = datas.filter(item => {
      for (const key in item) {
        if (
          typeof item[key] === 'string' &&
          key !== 'active' &&
          item[key].toLowerCase().indexOf(toTest) !== -1
        ) {
          return true;
        }
      }
      return false;
    });
    return filtered;
  };

  const isSavedValue = rowValue =>
    filterDelete(rowValue, savedValues).length !== savedValues.length;

  const setMarkDelete = (rowValue, marked) => {
    updateList(
      list.map(item =>
        item === rowValue ? { ...item, [MARK_DELETE_KEY]: marked } : item
      )
    );
  };

  const onCancelDelete = rowValue => {
    setMarkDelete(rowValue, false);
  };

  const onDelete = datas => {
    if (markDelete && isSavedValue(datas)) {
      setMarkDelete(datas, true);
      return;
    }

    const newList = filterDelete(datas, list);
    const filteredNewList = filterData(newList);
    if (page > Math.ceil(filteredNewList.length / rowPerPage)) {
      if (Math.ceil(filteredNewList.length / rowPerPage) === 0) {
        setPage(1);
      } else {
        setPage(Math.ceil(filteredNewList.length / rowPerPage));
      }
    }
    updateList(newList);
  };

  const onUpdate = item => {
    listUpdate[JSON.stringify(item)] = JSON.parse(JSON.stringify(item)); // TODO A RENDRE MOINS MOCHE
    setListUpdate({ ...listUpdate });
  };

  const handleChangeUpdate = item => (itemKey, isArray) => value => {
    const text = typeof value === 'string' ? value.trim() : value;
    if (isArray) {
      if (!item[itemKey]) {
        // eslint-disable-next-line no-param-reassign
        item[itemKey] = [];
      }
      if (text) {
        item[itemKey].push(text);
        // eslint-disable-next-line no-param-reassign
        delete item.tmp;
        setListUpdate({ ...listUpdate });
      }
      return;
    }
    // eslint-disable-next-line no-param-reassign
    item[itemKey] = text;
    setListUpdate({ ...listUpdate });
  };

  const onUpdateField = keyItem => () => {
    let block = false;
    const item = listUpdate[keyItem];
    options.forEach((el, index) => {
      if (inputs[index] === 'TextFieldArray' && item.tmp) {
        if (!item[el]) {
          item[el] = [];
        }
        item[el].push(item.tmp);
        delete item.tmp;
      }
      if (
        requireds[index] &&
        (typeof item[el] === 'undefined' || item[el].length === 0)
      ) {
        block = true;
      }
    });

    if (!block) {
      const index = list.findIndex(el => keyItem === JSON.stringify(el));
      list[index] = {
        ...listUpdate[keyItem]
      };
      delete listUpdate[keyItem];
      updateList([...list]);
    }
  };

  const handleTmpChange = item => value => {
    if (typeof value === 'undefined') {
      return;
    }
    const text = value.trim();
    // eslint-disable-next-line no-param-reassign
    item.tmp = text;
  };

  const getValueUnique = ({
    value,
    fieldsFormat: fieldsFormats,
    optionIndex
  }) => {
    return fieldsFormats.filter(fieldFormat => {
      const find = list.find(el => el[optionIndex] === fieldFormat.value);
      if (!find || find[optionIndex] === value) {
        return true;
      }
      return false;
    });
  };

  const showAction = keyEnter
    ? true
    : !selectNbMax || list.length < selectNbMax;

  const getRowList = (rowItem = {}, value) => {
    const formatValue = (item, key, unit = '') => {
      if (Array.isArray(item)) {
        return item.map(el => el?.value ?? el?.key ?? el).join(', ') + unit;
      }

      const newValue = item?.[key] || item?.label || item;
      if (React.isValidElement(newValue)) {
        return newValue;
      }
      return `${newValue}${unit}`;
    };

    const isMarkedForDelete = !!(markDelete && rowItem[MARK_DELETE_KEY]);

    return (
      <Row spacing={0} justify="flex-start">
        <Item xs={rowItem?.itemSize || 12}>
          <Paper
            elevation={0}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '8px',
              backgroundColor: isMarkedForDelete ? ds.bgSoft : undefined,
              border: isMarkedForDelete
                ? `1px dashed ${ds.red}`
                : `1px solid ${ds.line}`,
              borderRadius: ds.radius,
              boxShadow: 'none'
            }}
          >
            <Row spacing={0} alignItems="center">
              {Object.keys(rowItem).reduce((acc, key, index) => {
                if (
                  hideKeys.includes(key) ||
                  key === 'itemSize' ||
                  key === MARK_DELETE_KEY
                ) {
                  return acc;
                }
                const item = formatedValues?.[index] || list[index];
                const unit = units?.[index] || '';
                let el =
                  inputs &&
                  inputs[index] === 'Select' &&
                  Array.isArray(fieldsFormat[index])
                    ? fieldsFormat[index].find(
                        elem => elem.value === rowItem[key]
                      )?.label || rowItem[key]
                    : rowItem[key];
                if (Array.isArray(item)) {
                  el =
                    item?.find(formatedValue => formatedValue.value === el)
                      ?.label || rowItem[key];
                }

                let textColor = index === 0 ? ds.ink : ds.muted;
                if (isMarkedForDelete) {
                  textColor = ds.faint;
                }

                acc.push(
                  el !== rowItem?.warning && (
                    <Item xs justify="flex-start">
                      {typeof el === 'boolean' ? (
                        fieldsFormat[
                          options.findIndex(
                            option => option === Object.keys(rowItem)[index]
                          )
                        ][el ? 1 : 0]
                      ) : (
                        <Text
                          bold={index === 0}
                          fontSize="15px"
                          color={textColor}
                          style={
                            isMarkedForDelete
                              ? { textDecoration: 'line-through' }
                              : undefined
                          }
                        >
                          {formatValue(el, key, unit)}
                        </Text>
                      )}
                    </Item>
                  )
                );
                return acc;
              }, [])}
              {!disabled &&
                !isMarkedForDelete &&
                !['autocomplete', 'asyncAutoComplete'].includes(variant) && (
                  <Item flex>
                    <IconButton
                      onClick={() => {
                        onUpdate(value);
                      }}
                    >
                      <CreateIcon style={{ color: ds.faint }} />
                    </IconButton>
                  </Item>
                )}
              {!disabled && handleFavorite && value._id !== favoriteValue && (
                <Item xs={4}>
                  <Button
                    variant="text"
                    onClick={() => {
                      handleFavorite(value._id);
                    }}
                  >
                    Définir comme favorite
                  </Button>
                </Item>
              )}
              {!disabled && handleFavorite && value._id === favoriteValue && (
                <Item xs={4}>
                  <Text color={themeColors.global} fontWeight={700}>
                    Favorite
                  </Text>
                </Item>
              )}
              {!disabled && deleteAction && !isMarkedForDelete && (
                <Item flex style={{ paddingLeft: '8px' }}>
                  <IconButton
                    disabled={disabled}
                    onClick={() => {
                      onDelete(value);
                    }}
                  >
                    {deleteIcon || (
                      <DeleteOutlineIcon
                        style={{
                          color: ds.faint,
                          fontSize: '20px'
                        }}
                      />
                    )}
                  </IconButton>
                </Item>
              )}
              {!disabled && deleteAction && isMarkedForDelete && (
                <Item flex style={{ paddingLeft: '8px' }}>
                  <Chip
                    size="small"
                    label="À supprimer"
                    style={{
                      backgroundColor: ds.redBg,
                      color: ds.red,
                      marginRight: '8px'
                    }}
                  />
                  <Button
                    color={ds.ink}
                    onClick={() => {
                      onCancelDelete(value);
                    }}
                    style={{ textDecoration: 'underline' }}
                  >
                    Annuler
                  </Button>
                </Item>
              )}
            </Row>
          </Paper>
        </Item>
        {useWarning && rowItem.warning && (
          <Item>
            <Text bold color={warningColor}>
              Attention : &nbsp;
              <Text display="inline" color="inherit">
                {rowItem.warning}
              </Text>
            </Text>
          </Item>
        )}
      </Row>
    );
  };

  const getFieldFormat = (index, item) => {
    const format = fieldsFormat?.[index];
    return typeof format === 'function' ? format(item) : format;
  };

  const getField = (onHandleChange, onAdd, onDeleteArray, item) => {
    const inputWrapperStyle = {
      minHeight: 40,
      display: 'flex',
      alignItems: 'center'
    };
    return (
      <Container>
        <Row spacing={1}>
          <Item xs={11} justify="flex-start">
            <Container>
              <Row spacing={3} alignItems="flex-start" justify="flex-start">
                {options.map((el, index) => (
                  <Item
                    xs={options.length > 4 ? 4 : 12 / options.length}
                    justify="flex-start"
                    alignItems="flex-start"
                    style={{ paddingTop: 0 }}
                  >
                    {/* <Text noWrap>{labels?.[index] || el}</Text> */}
                    {inputs[index] === 'TextField' && (
                      <Item style={inputWrapperStyle}>
                        <TextField
                          small
                          title={labels?.[index] || el}
                          required={requireds[index]}
                          onChange={onHandleChange(el)}
                          format={fieldsFormat && fieldsFormat[index]}
                          unit={units?.[index]}
                          bgColor={bgColor}
                          onEnter={inputs.length === 1 ? onAdd : undefined}
                          value={(item && item[el]) || ''}
                          tooltip={tooltips[index]}
                          disabled={disabled}
                        />
                      </Item>
                    )}
                    {inputs[index] === 'TextFieldArray' && (
                      <Item>
                        <Row spacing={0}>
                          <Item style={inputWrapperStyle}>
                            <TextField
                              small
                              title={labels?.[index] || el}
                              required={requireds[index]}
                              format={fieldsFormat && fieldsFormat[index]}
                              unit={units?.[index]}
                              bgColor={bgColor}
                              value={fields?.tmp}
                              onChange={handleTmpChange(item)}
                              onEnter={onHandleChange(el, true)}
                              tooltip={tooltips[index]}
                              cleanOnEnter
                              disabled={disabled}
                            />
                          </Item>
                          <Item>
                            {item[el] &&
                              Array.isArray(item[el]) &&
                              item[el].map(value => (
                                <Chip
                                  variant="outlined"
                                  label={value}
                                  onDelete={onDeleteArray(el, value)}
                                  color="primary"
                                />
                              ))}
                          </Item>
                        </Row>
                      </Item>
                    )}
                    {inputs[index] === 'LocationSearchInput' && (
                      <Item style={inputWrapperStyle}>
                        <LocationSearchInput
                          title={labels?.[index] || el}
                          color={themeColors.global}
                          adr={item?.[el]?.address ?? ''}
                          required={requireds[index]}
                          onPlaceSelected={({ lat, lng, address }) => {
                            onHandleChange(el)({
                              lat,
                              lng,
                              address
                            });
                          }}
                          disabled={disabled}
                          optionWidth="250px"
                        />
                      </Item>
                    )}
                    {inputs[index] === 'Select' && (
                      <Item justify="flex-start" style={inputWrapperStyle}>
                        <Select
                          small
                          title={labels?.[index] || el}
                          required={requireds[index]}
                          tooltip={tooltips[index]}
                          onChange={onHandleChange(el)}
                          options={
                            valueUnique
                              ? getValueUnique({
                                  value: item[el]?.value || item[el],
                                  fieldsFormat: getFieldFormat(index, item),
                                  optionIndex: options[index] || 'value'
                                })
                              : getFieldFormat(index, item)
                          }
                          value={item[el]?.value || item[el]?.label || item[el]}
                          bgColor={bgColor}
                          disabled={disabled}
                        />
                      </Item>
                    )}
                    {inputs[index] === 'MultiSelect' && (
                      <Item justify="flex-start" style={inputWrapperStyle}>
                        <CustomSelect
                          small
                          title={labels?.[index] || el}
                          options={withSelectedOptions(
                            valueUnique
                              ? getValueUnique({
                                  value: item[el]?.value || item[el],
                                  fieldsFormat: getFieldFormat(index, item),
                                  optionIndex: options[index] || 'value'
                                })
                              : getFieldFormat(index, item),
                            item[el]
                          )}
                          selected={
                            item[el]?.value || item[el]?.label || item[el] || []
                          }
                          onChange={items => {
                            onHandleChange(el)(
                              items.filter(value => value.key !== 'selectAll')
                            );
                          }}
                          selectAll
                          hideNone
                          searchBar
                          textColor={themeColors.global}
                          multiple
                        />
                      </Item>
                    )}
                    {inputs[index] === 'Switch' && (
                      <Item justify="flex-start" style={inputWrapperStyle}>
                        <Switch
                          small
                          title={labels?.[index] || el}
                          tooltip={tooltips[index]}
                          required={requireds[index]}
                          onChange={() => onHandleChange(el)(!item[el])}
                          checked={item[el]}
                          disabled={disabled}
                          options={
                            fieldsFormat[index]?.length && fieldsFormat[index]
                          }
                        />
                      </Item>
                    )}
                    {inputs[index] === 'DatePicker' && (
                      <Item justify="flex-start">
                        <DatePicker
                          bgColor={bgColor}
                          title={labels?.[index] || el}
                          required={requireds[index]}
                          onClose={({ date }) =>
                            onHandleChange(el)(date?.format('YYYY-MM-DD') || '')
                          }
                          initialDate={
                            moment(item[el], 'YYYY-MM-DD').isValid()
                              ? moment(item[el], 'YYYY-MM-DD')
                              : undefined
                          }
                          disabled={disabled}
                        />
                      </Item>
                    )}
                  </Item>
                ))}
              </Row>
            </Container>
          </Item>
          <Item xs={1}>
            <Container>
              <Row spacing={3}>
                <Item>
                  <Button onClick={onAdd}>
                    <CheckIcon
                      style={{
                        color: ds.faint
                      }}
                    />
                  </Button>
                </Item>
              </Row>
            </Container>
          </Item>
        </Row>
      </Container>
    );
  };

  const getRowUpdate = (item, keyItem) => {
    return getField(
      handleChangeUpdate(item),
      onUpdateField(keyItem),
      handleDeleteUpdate(item),
      item
    );
  };

  const getActionRow = () => {
    return (
      <Row spacing={0} justify="flex-start">
        <Item justify="flex-start" xs={variant === 'customFields' ? 12 : 5}>
          {actionTxt &&
            !action &&
            (!['autocomplete'].includes(variant) || keyEnter
              ? true
              : list.length !== options.length) && (
              <Button
                disabled={disabled}
                onClick={onClick}
                variant="outlined"
                size="small"
                startIcon={
                  <AddIcon style={{ color: ds.ink, fontSize: '14px' }} />
                }
                sx={{
                  '& .MuiButton-startIcon': { marginRight: '4px' }
                }}
                style={{
                  backgroundColor: themeColors.keep_light,
                  border: `1px solid ${ds.line}`,
                  borderRadius: ds.radius,
                  color: ds.ink,
                  fontWeight: 500,
                  padding: '4px 10px',
                  lineHeight: '1.6',
                  fontSize: '12.5px'
                }}
              >
                {actionTxt}
              </Button>
            )}
          {(!actionTxt || action) && variant === 'autocomplete' && (
            <AutocompleteTextField
              options={options}
              formatLabel={option => {
                let str = option[searchOptionsKeys[0]];
                if (searchOptionsKeys[1]) {
                  str += ` (${option[searchOptionsKeys[1]]})`;
                }
                return str;
              }}
              exclude={option =>
                list.find(
                  el =>
                    el[searchOptionsKeys[0]] === option[searchOptionsKeys[0]]
                )
              }
              small
              bgColor={bgColor}
              onChange={onAutoCompliteChange}
              placeholder={placeholder}
              functionKeyEnter={newData => {
                if (newData) {
                  onAutoCompliteChange({
                    tag: newData
                  });
                }
              }}
              keyEnter={keyEnter}
            />
          )}

          {(!actionTxt || action) && variant === 'asyncAutoComplete' && (
            <AsyncAutocomplete
              onChange={onAutoCompliteChange}
              placeholder={placeholder}
              onChangeInput={onChangeInput}
              options={options}
              small
              bgColor={bgColor}
              exclude={option =>
                list.find(
                  el =>
                    el[searchOptionsKeys[0]] === option[searchOptionsKeys[0]]
                )
              }
              formatLabel={option => {
                let str = option[searchOptionsKeys[0]];
                if (searchOptionsKeys[1]) {
                  str += ` (${option[searchOptionsKeys[1]]})`;
                }
                return str;
              }}
            />
          )}

          {(!actionTxt || action) &&
            variant === 'customFields' &&
            getField(handleChange, onAddFields, handleDelete, fields)}
        </Item>
        <Item xs />
      </Row>
    );
  };

  const items = filterData(list);
  const showPaginations = items.length > rowPerPage;
  return (
    <Container justify="flex-start">
      {showAction && textFieldOnTop && getActionRow()}
      {list.length > rowPerPage && (
        <Row justify="flex-start" spacing={0}>
          <Item justify="flex-start" xs={6}>
            <TextField
              type="search"
              small
              placeholder={placeholder}
              onChange={onSearchTxtChange}
              value={searchTxt}
              title="Rechercher"
              unit={
                <SearchIcon
                  style={{
                    color: ds.faint
                  }}
                />
              }
            />
          </Item>
        </Row>
      )}
      {items
        .slice((page - 1) * rowPerPage, (page - 1) * rowPerPage + rowPerPage)
        .map(item => {
          const value = typeof item === 'string' ? { item } : item;
          try {
            const keyItem = JSON.stringify(value);
            if (listUpdate[keyItem]) {
              return getRowUpdate(listUpdate[keyItem], keyItem);
            }
          } catch (e) {
            console.error(e);
          }
          return getRowList(value, item);
        })}
      {showPaginations && (
        <Pagination
          count={Math.ceil(items.length / rowPerPage)}
          page={page}
          onChange={handlePageChange}
        />
      )}
      {showAction && !textFieldOnTop && getActionRow()}
    </Container>
  );
};

ListField.defaultProps = {
  deleteAction: false,
  markDelete: false,
  textFieldOnTop: false,
  useWarning: false,
  actionTxt: null,
  warningColor: null,
  formatedValues: null,
  tooltips: [],
  inputs: null,
  isRequireds: [true],
  labels: null,
  units: null,
  selectNbMax: null,
  deleteIcon: null,
  placeholder: null,
  fieldsFormat: null,
  deleteFunc: undefined,
  variant: 'autocomplete',
  sortValues: list => list,
  bgColor: null,
  hideKeys: [],
  valueUnique: false,
  keyEnter: false,
  onChangeInput: () => {}
};
ListField.propTypes = {
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  labels: PropTypes.arrayOf(PropTypes.any),
  units: PropTypes.arrayOf(PropTypes.any),
  searchOptionsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.any).isRequired,
  formatedValues: PropTypes.arrayOf(PropTypes.any),
  fieldsFormat: PropTypes.arrayOf(PropTypes.string),
  tooltips: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  actionTxt: PropTypes.string,
  onChangeInput: PropTypes.func,
  deleteAction: PropTypes.bool,
  markDelete: PropTypes.bool,
  sortValues: PropTypes.func,
  textFieldOnTop: PropTypes.bool,
  useWarning: PropTypes.bool,
  warningColor: PropTypes.string,
  selectNbMax: PropTypes.number,
  deleteIcon: PropTypes.node,
  deleteFunc: PropTypes.func,
  placeholder: PropTypes.string,
  bgColor: PropTypes.string,
  variant: PropTypes.string,
  inputs: PropTypes.arrayOf(PropTypes.string),
  hideKeys: PropTypes.arrayOf(PropTypes.string),
  isRequireds: PropTypes.arrayOf(PropTypes.bool),
  valueUnique: PropTypes.bool,
  keyEnter: PropTypes.bool
};

export default ListField;
